import { useEffect, useMemo, useState } from 'react';
import { listPaymentModes, archivePaymentMode, deletePaymentMode } from '../api/paymentModeApi';
import { appConfirm } from '../../../../components/AppConfirmModal';
import { useExportJob } from '../../../../hooks/useExportJob';

const DEFAULT_PAGE_SIZE = 10;

export function usePaymentModeListScreen() {
  const { triggerExport, isExporting } = useExportJob({
    module: 'payment_mode',
    fileFormat: 'XLSX',
  });

  const [statusFilter, setStatusFilter] = useState('Active');
  const [searchField, setSearchField] = useState('Name');
  const [searchText, setSearchText] = useState('');
  
  const [page, setPage] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({
    totalCount: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    hasMore: false,
  });

  const queryParams = useMemo(() => {
    return {
      page,
      page_size: pageSize,
      viewMode: statusFilter.toLowerCase(),
      searchText,
    };
  }, [page, pageSize, statusFilter, searchText]);

  const loadPaymentModes = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await listPaymentModes(queryParams);
      const totalCount = response.length;
      const start = (page - 1) * pageSize;
      const paginated = response.slice(start, start + pageSize);
      
      setItems(paginated);
      setMeta({
        totalCount,
        page,
        pageSize,
        hasMore: start + pageSize < totalCount,
      });
    } catch (requestError) {
      setError('Failed to load payment modes.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPaymentModes();
  }, [queryParams]);

  const handleArchive = async (id) => {
    try {
      await archivePaymentMode(id);
      loadPaymentModes();
    } catch (e) {
      setError('Failed to update payment mode status.');
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await appConfirm({
      title: 'Delete Payment Mode',
      message: 'Are you sure you want to permanently delete this payment mode?',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await deletePaymentMode(id);
      loadPaymentModes();
    } catch (e) {
      setError('Failed to delete payment mode.');
    }
  };

  const resetFilters = () => {
    setStatusFilter('Active');
    setSearchField('Name');
    setSearchText('');
    setPage(1);
  };

  const actions = {
    setStatusFilter,
    setSearchField,
    setSearchText,
    setPage,
    resetFilters,
    handleArchive,
    handleDelete,
    refresh: loadPaymentModes,
    triggerExport,
  };

  return {
    state: {
      statusFilter,
      searchField,
      searchText,
      page,
      pageSize,
      isLoading,
      isExporting,
      error,
      rows: items,
      meta,
    },
    actions,
  };
}
